import { type ComponentPropsWithRef, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Typography, type TypographyProps } from '@/shared-ui/atoms/typography';

export type PriceAppearance = 'default' | 'sale' | 'disabled';

const appearanceTextClass: Record<PriceAppearance, string> = {
    default: 'text-foreground tracking-[1.44px]',
    sale: 'text-snk-discounted tracking-[1.44px]',
    disabled: 'text-muted-foreground tracking-[1.44px]',
};

/** Root `data-disabled` wins over sale / default colour via Tailwind group-data. */
const rootDisabledTextClass = 'group-data-[disabled=true]:text-muted-foreground';

// Root -----------------------------------------------------------------------

export type RootProps = ComponentPropsWithRef<'div'> & {
    /** When true, current / range prices use the disabled (out of stock) style. */
    disabled?: boolean;
};

const Root = ({ disabled = false, className, ref, children, ...props }: RootProps) => (
    <div
        data-slot="price"
        data-disabled={disabled ? 'true' : undefined}
        ref={ref}
        className={cn('group flex flex-col items-start gap-0.5', className)}
        {...props}>
        {children}
    </div>
);
Root.displayName = 'Price.Root';

// Group ----------------------------------------------------------------------

/** Horizontal row for side-by-side amounts (for example original + current). */
export type GroupProps = ComponentPropsWithRef<'div'>;

const Group = ({ className, ref, ...props }: GroupProps) => (
    <div
        data-slot="price-group"
        ref={ref}
        className={cn('inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5', className)}
        {...props}
    />
);
Group.displayName = 'Price.Group';

// Current --------------------------------------------------------------------

export type CurrentProps = Omit<TypographyProps, 'variant' | 'as' | 'children'> & {
    children: ReactNode;
    /** Visual treatment. Root `disabled` overrides colour via `group-data-[disabled=true]`. */
    appearance?: PriceAppearance;
};

const Current = ({ appearance = 'default', className, ref, children, ...props }: CurrentProps) => (
    <Typography
        data-slot="price-current"
        data-appearance={appearance}
        variant="product-price"
        weight="regular"
        as="span"
        ref={ref}
        className={cn('tabular-nums', appearanceTextClass[appearance], rootDisabledTextClass, className)}
        {...props}>
        {children}
    </Typography>
);
Current.displayName = 'Price.Current';

// Original -------------------------------------------------------------------

export type OriginalProps = Omit<TypographyProps, 'variant' | 'as' | 'children'> & {
    children: ReactNode;
};

/** Struck-through list / compare-at price. Prefer pairing with `Price.Current appearance="sale"`. */
const Original = ({ className, ref, children, ...props }: OriginalProps) => (
    <Typography
        data-slot="price-original"
        variant="product-price"
        as="span"
        weight="regular"
        ref={ref}
        className={cn('tabular-nums text-muted-foreground line-through', className)}
        {...props}>
        {children}
    </Typography>
);
Original.displayName = 'Price.Original';

// Range ----------------------------------------------------------------------

export type RangeProps = Omit<TypographyProps, 'variant' | 'as' | 'children'> & {
    from: ReactNode;
    to: ReactNode;
    /** Separator between bounds. Defaults to a hyphen with a space on each side. */
    separator?: ReactNode;
    appearance?: PriceAppearance;
};

const toAccessibleText = (node: ReactNode): string | undefined => {
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }
    return undefined;
};

/** Builds a single announcement for the range when bounds/separator are plain text. */
const buildRangeAccessibleLabel = (from: ReactNode, to: ReactNode, separator: ReactNode): string | undefined => {
    const fromText = toAccessibleText(from);
    const toText = toAccessibleText(to);
    const separatorText = toAccessibleText(separator);
    if (fromText === undefined || toText === undefined || separatorText === undefined) {
        return undefined;
    }
    return `${fromText}${separatorText}${toText}`;
};

const Range = ({
    from,
    to,
    separator = ' - ',
    appearance = 'default',
    className,
    ref,
    'aria-label': ariaLabelProp,
    ...props
}: RangeProps) => {
    const accessibleLabel = ariaLabelProp ?? buildRangeAccessibleLabel(from, to, separator);
    // When labeled, hide visual children so AT does not announce the prices twice.
    // When unlabeled (non-text nodes), keep the separator exposed as a spoken delimiter.
    const hideVisualFromAssistiveTech = Boolean(accessibleLabel);

    return (
        <Typography
            data-slot="price-range"
            data-appearance={appearance}
            variant="product-price"
            as="span"
            weight="regular"
            ref={ref}
            aria-label={accessibleLabel}
            className={cn(
                'inline-flex items-baseline tabular-nums',
                appearanceTextClass[appearance],
                rootDisabledTextClass,
                className
            )}
            {...props}>
            <span data-slot="price-range-from" aria-hidden={hideVisualFromAssistiveTech || undefined}>
                {from}
            </span>
            <span
                data-slot="price-range-separator"
                aria-hidden={hideVisualFromAssistiveTech || undefined}
                className="whitespace-pre">
                {separator}
            </span>
            <span data-slot="price-range-to" aria-hidden={hideVisualFromAssistiveTech || undefined}>
                {to}
            </span>
        </Typography>
    );
};
Range.displayName = 'Price.Range';

// SubMessage -----------------------------------------------------------------

export type SubMessageProps = Omit<TypographyProps, 'variant' | 'as' | 'children'> & {
    children: ReactNode;
};

/** Promotional or secondary line under the price (for example promo-code copy). */
const SubMessage = ({ className, ref, children, ...props }: SubMessageProps) => (
    <Typography
        data-slot="price-sub-message"
        variant="caption"
        as="span"
        weight="regular"
        ref={ref}
        className={cn('text-snk-status-success-dark', className)}
        {...props}>
        {children}
    </Typography>
);
SubMessage.displayName = 'Price.SubMessage';

// Namespace ------------------------------------------------------------------

const Price = {
    Root,
    Group,
    Current,
    Original,
    Range,
    SubMessage,
} as const;

export { Price };
